<template>
  <div v-bind="attributes.top" class="c-bitts-textarea">
    <BittsFormLabel
      v-if="formLabel"
      class="c-bitts-textarea__label"
      :label="formLabel"
      :disabled="disabled"
      :for="textAreaId"
    />
    <Input.TextArea
      ref="textareaRef"
      v-bind="{ ...attributes.input, ...ariaAttrs }"
      :id="textAreaId"
      :disabled="disabled"
      :placeholder="placeholder"
      :value="modelValue"
      :rows="rowCount"
      class="c-bitts-textarea"
      :class="{
        danger: status === 'danger',
        success: status === 'success',
        disabled: disabled,
      }"
      @input="emit('update:modelValue', $event.target.value)"
    />
    <BittsStatus
      v-if="status !== 'default'"
      :status="status"
      :disabled="disabled"
      :success-text="successText"
      :danger-text="dangerText"
      :message-id="messageId"
    />
  </div>
</template>
<script setup lang="ts">
import { Input } from 'ant-design-vue';
import { computed, ref, useAttrs, useId } from 'vue';

import BittsFormLabel from '../BittsFormLabel/BittsFormLabel.vue';
import { FormLabelType } from '../BittsFormLabel/types';
import BittsStatus, {
  BittsStatusDefaults,
  BittsStatusProps,
} from '../BittsStatus/BittsStatus.vue';
import { useStatusAria } from '../composables/useStatusAria';

export type Props = BittsStatusProps & {
  modelValue?: string;
  disabled?: boolean;
  rowCount?: number;
  placeholder?: string;
  formLabel?: FormLabelType | string;
};

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  rowCount: 4,
  placeholder: '',
  formLabel: '',
  ...BittsStatusDefaults,
});

const emit = defineEmits(['update:modelValue']);

defineOptions({ inheritAttrs: false });
const rawAttrs = useAttrs();
const generatedId = useId();
const textAreaId = computed(
  () => (rawAttrs.id as string | undefined) ?? generatedId,
);
const attributes = computed(() => {
  const { class: className, id: _id, ...rest } = rawAttrs;
  return { top: { class: className }, input: { ...rest } };
});
const { messageId, ariaAttrs } = useStatusAria(() => ({
  status: props.status,
  isDisabled: props.disabled,
  ariaDescribedby: rawAttrs['aria-describedby'],
}));

const textareaRef = ref<
  (InstanceType<typeof Input.TextArea> & { focus?: () => void }) | null
>(null);

function focus() {
  textareaRef.value?.focus?.();
}

defineExpose({ focus });
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.c-bitts-textarea {
  font-family: inherit;
}
textarea.c-bitts-textarea.ant-input::placeholder {
  @apply text-neutral-text-placeholder;
}

textarea.c-bitts-textarea.ant-input {
  @apply rounded-8 border-neutral-border shadow-component outline w-full py-10 min-h-[45px] text-neutral-text outline-transparent;
  font-family: inherit;

  &:hover,
  &:focus {
    @apply outline border-neutral-border-focus outline-neutral-border-focus;
  }

  &.disabled {
    @apply bg-neutral-background-disabled outline-neutral-border border-neutral-border;
    &:hover,
    &:focus {
      @apply outline-0;
    }
  }

  &.success:not(.disabled) {
    @apply outline-success-accent border-success-accent outline;
  }

  &.danger:not(.disabled) {
    @apply border-danger-accent outline-danger-accent outline;
  }
}
</style>
